<template>
    <div>
        <div class="flex flex-col gap-3 mb-4 | lg:flex-row lg:items-center">
            <span data-testid="search-lead-count" v-if="!isFetchingLeads">
                {{
                    t('searchLead.tableFooter', {
                        limit: searchCount,
                        leadCount: totalLeadCount,
                    })
                }}
            </span>
            <activix-input
                class="text-grey-700 w-full | lg:w-5/12 lg:ml-auto"
                input-class="rounded-lg"
                :placeholder="t('searchLead.title')"
                data-testid="search-lead-input"
                @input="handleSearch"
                v-model="searchString"
            >
                <template #icon>
                    <icon class="text-grey-700" name="regular/search" />
                </template>
            </activix-input>
        </div>
        <div
            class="flex grow justify-center items-center"
            data-testid="search-lead-spinner"
            v-if="isFetchingLeads"
        >
            <activix-spinner class="sticky bottom-6 my-6" :size="50" />
        </div>
        <div class="w-full" :class="{ 'overflow-x-auto': mdLayout }" v-else>
            <table
                class="table table-bordered table-striped | border"
                data-testid="search-lead-table"
                cellspacing="0"
                width="100%"
            >
                <thead>
                    <tr>
                        <th>{{ t('searchLead.client_name') }}</th>
                        <th>{{ t('searchLead.lead_type') }}</th>
                        <th>{{ t('searchLead.date') }}</th>
                        <th>{{ t('searchLead.division') }}</th>
                        <th class="w-px" />
                    </tr>
                </thead>
                <tbody>
                    <tr data-testid="search-lead-row" :key="lead.id" v-for="lead in filteredLeads">
                        <td>
                            <router-link
                                target="_blank"
                                :to="{
                                    name: 'leads.update',
                                    params: { id: lead.id },
                                }"
                                v-if="lead.id"
                            >
                                {{ getFullName(lead) }}
                            </router-link>
                        </td>
                        <td>{{ formatLeadType(lead) }}</td>
                        <td nowrap>
                            {{ formatDate(lead) }}
                        </td>
                        <td>{{ formatDivision(lead) }}</td>
                        <td class="text-center">
                            <activix-button
                                :data-testid="`search-lead-choose-button-${lead.id}`"
                                size="small"
                                :loading="leadLoading == lead.id"
                                :disable="!!leadLoading"
                                @click="$emit('select-lead', lead.id)"
                            >
                                {{ t('searchLead.chooseLead') }}
                            </activix-button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
    import { camelCase, debounce } from 'lodash-es';
    import { useI18n } from '@/hooks/useI18n.ts';
    import LeadType from '../../entities/LeadType.js';
    import Division from '../../entities/Division.js';

    export default {
        emits: ['select-lead', 'search'],

        props: {
            leads: {
                type: Array,
                required: true,
            },
            leadLoading: {
                type: null,
                required: true,
            },
            potentialLeadCount: {
                type: Number,
                default: null,
            },
            potentialLeadCountIsCapped: {
                type: Boolean,
                default: false,
            },
        },

        setup() {
            const { t } = useI18n();

            return {
                t,
            };
        },

        data() {
            return {
                limit: 10,
                searchString: '',
                debouncedSearch: null,
            };
        },

        computed: {
            isFetchingLeads() {
                return this.$wait.is('fetching.taskEventLeads');
            },

            filteredLeads() {
                return this.leads.slice(0, this.limit);
            },

            searchCount() {
                return this.filteredLeads.length < this.limit
                    ? this.filteredLeads.length
                    : this.limit;
            },

            totalLeadCount() {
                if (this.potentialLeadCount == null) {
                    return this.leads.length;
                }

                if (this.potentialLeadCountIsCapped) {
                    return `${this.potentialLeadCount}+`;
                }

                return this.potentialLeadCount;
            },
        },

        methods: {
            handleSearch(event) {
                const value = typeof event === 'string' ? event : event?.target?.value ?? '';
                this.debouncedSearch(value);
            },

            formatLeadType(lead) {
                if (!lead.lead_type_id) {
                    return '';
                }

                const key = camelCase(LeadType.getKey(lead.lead_type_id));

                if (!key) {
                    return '';
                }

                return this.t(`leadTypes.${key}`);
            },

            formatDate(lead) {
                return as_locale(lead.created_at, 'created_at').humanShort();
            },

            formatDivision(lead) {
                if (!lead.division_id) {
                    return '';
                }

                const key = camelCase(Division.getKey(lead.division_id));

                return this.t(`divisions.${key}`);
            },
        },

        created() {
            this.debouncedSearch = debounce((search) => {
                this.$emit('search', search);
            }, 800);
        },
    };
</script>
